import { P2GAlert, P2GBadge } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';

import { P2GLink } from '@parcel2go/core-ui';
import { P2GFade, P2GGrow } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { ReactNode } from 'react';
import { ConsignmentApiModel } from '~/lib/api/smartsend/types';
import SvgLabelPrinting from '../../../../../../components/icons/SvgLabelPrinting';
import { SmartSendItemGroup } from '../../item/SmartSendGroup';
import { GroupSummary } from '../GenerateGroupSummary';
import { SplitWithDropDown } from '../SplitWithDropDown';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

export interface props {
  consignment: ConsignmentApiModel;
  onClose: () => void;
  groupSummary: GroupSummary;
  consignmentParcels: number;
  expand: boolean;
  setExpand: (e: boolean) => void;
  generateConsignment: (x: number) => void;
  group: SmartSendItemGroup;
  serviceSupportsConsignments: boolean;
  splitConsignmentControl: ReactNode;
  valid: boolean;
  children?: ReactNode;
}

const ConsignmentSummaryMessage: React.FC<{ valid: boolean; children?: ReactNode }> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (!props.valid) {
    return (
      <P2GBox pt={1}>
        <P2GFade in>
          <P2GText variant="body2" component="p" colour="error">
            {t('ENTER_WEIGHT_ALERT', { defaultValue: 'Please enter a weight to see shipment options.' })}
          </P2GText>
        </P2GFade>
      </P2GBox>
    );
  }
  return (
    <P2GFade in>
      <>{props.children}</>
    </P2GFade>
  );
};

export const ConsignmentSummary: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GAlert
      icon={
        <P2GBadge badgeContent={props.consignmentParcels} colour="primary" key={props.consignmentParcels}>
          <P2GGrow in timeout={1200}>
            <CdnIconProxy src="/theme/icons/LabelPrinting.svg" size="md" palette="primary">
              <SvgLabelPrinting />
            </CdnIconProxy>
          </P2GGrow>
        </P2GBadge>
      }
    >
      {props.consignment ? (
        <P2GText variant="body2">
          <Trans
            i18nKey="CONSIGNMENT_SUMMARY_INTRO_TEXT"
            t={t}
            values={{
              parcelCount: props.consignmentParcels
            }}
            defaults="This will be sent as a <strong>{{parcelCount}} parcel</strong> shipment."
          />
          <br />
          {props.expand ? (
            t('CONSIGNMENT_SUMMARY_INTRO_SUBTEXT', { defaultValue: 'The contents of each parcel is shown below.' })
          ) : (
            <Trans
              i18nKey="CONSIGNMENT_SUMMARY_INTRO_SUBTEXT_ALTERNATIVE"
              t={t}
              components={{ a: <P2GLink onClick={() => props.setExpand(true)} /> }}
              values={{
                parcelCount: props.consignmentParcels
              }}
              defaults="The contents have been distributed equally. <a>Show Me</a>"
            />
          )}
          <ConsignmentSummaryMessage valid={props.valid}>
            <SplitWithDropDown
              groupingControl={props.splitConsignmentControl}
              selected={props.consignmentParcels}
              onClick={(q) => props.generateConsignment(q)}
              options={props.serviceSupportsConsignments ? props.groupSummary.totalQuantity : 0}
            >
              <P2GLink>{t('CHANGE_NUMBER_OF_PARCELS_LINK', { defaultValue: 'Change Number of Parcels' })}</P2GLink>
            </SplitWithDropDown>
          </ConsignmentSummaryMessage>
        </P2GText>
      ) : (
        <P2GText variant="body2">
          <Trans
            i18nKey="CONSIGNMENT_SUMMARY_MESSAGE"
            t={t}
            defaults="This will be sent as a <strong>single-parcel</strong> shipment."
          />
          <ConsignmentSummaryMessage valid={props.valid}>
            <SplitWithDropDown
              groupingControl={props.splitConsignmentControl}
              selected={props.consignmentParcels}
              onClick={(q) => props.generateConsignment(q)}
              options={props.serviceSupportsConsignments ? props.groupSummary.totalQuantity : 0}
            >
              <P2GLink>
                {t('SEND_AS_MULTIPLE_SHIPMENT_LINK', { defaultValue: 'Send as Multi-Parcel Shipment' })}
              </P2GLink>
              ,
            </SplitWithDropDown>
          </ConsignmentSummaryMessage>
        </P2GText>
      )}
    </P2GAlert>
  );
};
